import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AddressFragment, CountryFragment, OrderAddressFragment } from '../../../common/generated-types';
import { HIDDEN_PROVINCE_REGIONS } from '../../../common/constants';
import {
    AU_STATES,
    BR_STATES,
    CA_PROVINCES,
    CN_PROVINCES,
    IN_SUBDIVISIONS,
    IT_PROVINCES,
    JP_PREFECTURES,
    KR_PROVINCES,
    MX_STATES,
    MY_STATES,
    Subdivision,
    US_STATES
} from '../../../common/state-list';
import { parseFullName } from '../../../common/utils/parse-fullname';

export const internationalPhonePattern = /^\+?[0-9 .()\-\s]{7,20}$/;

const phoneValidator: ValidatorFn = (
    control: AbstractControl,
) => {
    const value = (control.value ?? '').normalize('NFKC').replace(/\p{Cf}/gu, '').trim();
    return !value || internationalPhonePattern.test(value)
      ? null
      : { phoneInvalid: true };
};

@Component({
    selector: 'vsf-address-form',
    templateUrl: './address-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressFormComponent implements OnChanges {

    @Input() availableCountries: CountryFragment[];
    @Input() address: OrderAddressFragment | AddressFragment;
    @Input() fullName: string;
    @Input() readonlyFullName = false;

    addressForm: UntypedFormGroup;
    availableStates: {code: string, name: string}[] = [];
    postalCodeNaming = 'ZIP code';
    stateNaming = 'State/Province/Region';
    displayState = true;
    isProvincePlaceholder = true;

    statesByCountry: { [countryCode: string]: Subdivision[] } = {
        'US': US_STATES,
        'CA': CA_PROVINCES,
        'AU': AU_STATES,
        'CN': CN_PROVINCES,
        'JP': JP_PREFECTURES,
        'MX': MX_STATES,
        'BR': BR_STATES,
        'MY': MY_STATES,
        'KR': KR_PROVINCES,
        'IN': IN_SUBDIVISIONS,
        'IT': IT_PROVINCES,
    };

    constructor(
        private formBuilder: UntypedFormBuilder,
        private changeDetector: ChangeDetectorRef,
    ) {
        this.addressForm = this.formBuilder.group({
            countryCode: ['US', Validators.required],
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            company: '',
            streetLine1: ['', Validators.required],
            streetLine2: '',
            city: ['', Validators.required],
            province: [''],
            postalCode: ['', Validators.required],
            phoneNumber: ['', [
                Validators.required,
                phoneValidator,
              ],
            ],
        });
        this.updateAvailableStates(this.addressForm.get('countryCode')?.value);
        const countryCtrl  = this.addressForm.get('countryCode');
        const provinceCtrl = this.addressForm.get('province');

        countryCtrl?.valueChanges.subscribe(countryCode => {
            this.updateAvailableStates(countryCode);
            const normalizedCountryCode = this.normalizeCountryCode(countryCode);
            this.displayState = !HIDDEN_PROVINCE_REGIONS.includes(normalizedCountryCode);
            if(!this.displayState) {
                provinceCtrl?.setValue('', { emitEvent: false });
            }

            const requiresState = (normalizedCountryCode === 'US' || this.availableStates.length > 0);
            if(requiresState) {
                provinceCtrl?.setValidators([Validators.required]);
            } else {
                provinceCtrl?.clearValidators();
            }
            provinceCtrl?.updateValueAndValidity({ emitEvent: false });
            provinceCtrl?.markAsUntouched();

            this.changeDetector.detectChanges();
        });
        provinceCtrl?.valueChanges.subscribe(v => {
            this.isProvincePlaceholder = !v;     // true when '', false when any real code
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('address' in changes && this.addressForm && this.address) {
            const { firstName, lastName } = parseFullName(this.address.fullName ?? '');
            this.addressForm.patchValue(this.address, { });
            this.addressForm.patchValue({
                firstName,
                lastName,
                company: this.address.company,
                streetLine1: this.address.streetLine1,
                streetLine2: this.address.streetLine2,
                city: this.address.city,
                province: this.address.province,
                postalCode: this.address.postalCode,
                phoneNumber: this.address.phoneNumber,
            });
        }
        const country = this.address && this.address.country;
        if (country && this.availableCountries) {
            if (country && typeof country !== 'string') {
                this.addressForm.patchValue({
                    countryCode: country.code,
                });
                this.updateAvailableStates(country.code);
            } else {
                const matchingCountry = this.availableCountries.find(c => c.name === country);
                if (matchingCountry) {
                    this.addressForm.patchValue({
                        countryCode: matchingCountry.code,
                    });
                    this.updateAvailableStates(matchingCountry.code);
                }
            }
        }
        this.changeDetector.detectChanges();
    }

    normalizeCountryCode(countryCode: string): string {
        const normalized = countryCode?.toUpperCase()?.replace(/\./g, '');
        if (['USA', 'US', 'U.S', 'U.S.', 'U.S.A'].includes(normalized)) {
            return 'US';
        }
        return countryCode;
    }

    updateAvailableStates(countryCode: string) {
        const normalizedCountryCode = this.normalizeCountryCode(countryCode);
        this.availableStates = this.statesByCountry[normalizedCountryCode] || [];
        const provinceControl = this.addressForm.get('province');
        if (this.availableStates.length === 0) {
            provinceControl?.enable();
        } else {
            const currentProvince = provinceControl?.value;
            if (currentProvince) {
                // Check if the current province is in the list of available states
                const stateMatch = this.availableStates.find(
                    state => state.code === currentProvince || state.name === currentProvince
                );
                if (stateMatch) {
                    provinceControl.setValue(stateMatch.code, { emitEvent: false });
                } else {
                    provinceControl.setValue('', { emitEvent: false });
                }
            } else {
                provinceControl?.setValue('', { emitEvent: false });
            }
            provinceControl?.enable();
        }
        provinceControl?.markAsUntouched();
    }

    public touchAllAndUpdate(): void {
        this.addressForm.markAllAsTouched();
        this.addressForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
        this.changeDetector.markForCheck();
    }
}
